import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { FORUM_STAFF_PAGE_DESCRIPTION } from '@/features/course-forum/constants';
import { CourseForumEmptyIllustration } from '@/features/course-forum/CourseForumEmptyIllustration';
import { CourseForumNewThreadModal } from '@/features/course-forum/CourseForumNewThreadModal';
import { CourseForumThreadBoard } from '@/features/course-forum/CourseForumThreadBoard';
import { type ForumOverview } from '@/features/course-forum/types';
import { api } from '@/lib/api';
import { courseForumThreadHref } from '@/lib/course-admin-navigation';
import { forumThreadCreateSuccessToast } from '@/lib/user-feedback';

interface CourseMeta {
  name: string;
}

type SortOrder = 'desc' | 'asc';

export function CourseForumModerationPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const { data: course } = useQuery({
    queryKey: ['courses', id],
    queryFn: () => api<CourseMeta>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data: forum, isLoading, error } = useQuery({
    queryKey: ['courses', id, 'forum'],
    queryFn: () => api<ForumOverview>(`/courses/${id}/forum`),
    enabled: Boolean(id),
  });

  const createMutation = useMutation({
    mutationFn: (payload: { title: string; body: string }) =>
      api<{ id: string }>(`/courses/${id}/forum/threads`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async (thread) => {
      setThreadModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['courses', id, 'forum'] });
      toast(forumThreadCreateSuccessToast());
      if (id && thread?.id) {
        navigate(courseForumThreadHref(id, thread.id, searchParams));
      }
    },
  });

  const threads = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = [...(forum?.threads ?? [])];

    if (query) {
      list = list.filter((thread) => {
        const haystack = [
          thread.title,
          thread.excerpt,
          thread.author.displayName,
          thread.author.anonAlias,
          thread.author.authorRealName,
          thread.author.authorEmail,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    list.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const delta = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return sortOrder === 'desc' ? delta : -delta;
    });

    return list;
  }, [forum?.threads, search, sortOrder]);

  if (!id) return null;

  const hasThreads = (forum?.threads?.length ?? 0) > 0;

  return (
    <CourseHubShell
      courseId={id}
      courseName={course?.name ?? 'Curso'}
      activeTab="foro"
      description={FORUM_STAFF_PAGE_DESCRIPTION}
    >
      <div className="lms-course-forum-page">
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        ) : null}

        {isLoading || !forum ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : !forum.enabled ? (
          <div className="lms-course-forum-disabled">
            El foro está deshabilitado para este curso. Actívalo en la configuración del curso.
          </div>
        ) : (
          <>
            {!hasThreads ? (
              <div className="lms-forum-flow">
                <div className="lms-forum-flow__bar">
                  <h2 className="lms-course-forum-card__title">Moderación del foro</h2>
                  <button
                    type="button"
                    className="lms-course-forum-btn lms-course-forum-btn--primary"
                    onClick={() => setThreadModalOpen(true)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      add
                    </span>
                    + Nuevo hilo como docente
                  </button>
                </div>
                <div className="lms-course-forum-empty-state">
                  <div className="lms-course-forum-empty-state__glow lms-course-forum-empty-state__glow--top" aria-hidden="true" />
                  <div className="lms-course-forum-empty-state__glow lms-course-forum-empty-state__glow--bottom" aria-hidden="true" />
                  <CourseForumEmptyIllustration />
                  <h2 className="lms-course-forum-empty-state__title">
                    Aún no hay hilos en este foro.
                  </h2>
                  <p className="lms-course-forum-empty-state__lead">
                    Publica anuncios o abre debates como docente. Los estudiantes participan con alias
                    anónimo y tú ves su identidad real en esta vista.
                  </p>
                  <div className="lms-course-forum-empty-state__actions">
                    <button
                      type="button"
                      className="lms-course-forum-btn lms-course-forum-btn--accent"
                      onClick={() => setThreadModalOpen(true)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        add_comment
                      </span>
                      Nuevo hilo como docente
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <CourseForumThreadBoard
                title="Foros"
                threads={threads}
                courseId={id}
                staffView
                search={search}
                onSearchChange={setSearch}
                sortOrder={sortOrder}
                onToggleSort={() => setSortOrder((value) => (value === 'desc' ? 'asc' : 'desc'))}
                onNewThread={() => setThreadModalOpen(true)}
                newThreadLabel="+ Nuevo hilo como docente"
                emptyMessage="No hay hilos que coincidan con tu búsqueda o filtros."
              />
            )}
          </>
        )}
      </div>

      <CourseForumNewThreadModal
        open={threadModalOpen}
        saving={createMutation.isPending}
        submitError={createMutation.error instanceof Error ? createMutation.error.message : null}
        title="Nuevo hilo como docente"
        onClose={() => {
          setThreadModalOpen(false);
          createMutation.reset();
        }}
        onSubmit={(payload) => createMutation.mutate(payload)}
      />
    </CourseHubShell>
  );
}
